import { CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { translations } from "../../../configs/translations"
import { useDriftHudState } from "../../../hooks/useDriftHudState"
import { BASE_URL } from "../../../services/api"
import { createAuthenticatedEventSource } from "../../../services/authenticatedEventSource"
import { ClientApi } from '../../../services/clientApi'
import { DriftService } from '../../../services/DriftService'
import { useStore } from "../../../store/store"
import { NuiMessages } from '../../../utils/ClientEvents'
import "./DriftHud.css"
import { DriftDto } from "./DriftHud.types"
import { PairDriftBadge } from './pairBadge/PairDriftBadge'

const getAchievementTitle = (
    rawTitle: string | undefined,
    t: typeof translations[keyof typeof translations]
) => {
    if (!rawTitle) return ""

    const [titleKey, gradeKey] = rawTitle.split(" ")

    const getGradeText = (gradeKey: string) => {
        const grade = parseInt(gradeKey)
        if (grade === 1) return t["technique"]
        if (grade === 2) return t["good_technique"]
        if (grade === 3) return t["excellent_technique"]
        if (grade === 4) return t["perfect_technique"]
        if (grade === 5) return t["master_technique_chain"]
        return gradeKey
    }

    const titleText = t[titleKey.toLocaleLowerCase() as keyof typeof t] || titleKey

    if (titleKey === "master_technique_chain") {
        return titleText
    }

    return `${getGradeText(gradeKey)} «${titleText}»`.trim()
}

const getShakeAnimationDelta = (points: number) => {
    const minPoints = 250
    const maxPoints = 40000
    const minDelta = 1
    const maxDelta = 5

    const progress = Math.min(
        Math.max((points - minPoints) / (maxPoints - minPoints), 0),
        1
    )

    return minDelta + (maxDelta - minDelta) * progress * progress
}

const getShakeAnimationDuration = (points: number) => {
    const minPoints = 250
    const maxPoints = 40000
    const minDuration = 350
    const maxDuration = 50

    const progress = Math.min(
        Math.max((points - minPoints) / (maxPoints - minPoints), 0),
        1
    )

    return minDuration - (minDuration - maxDuration) * progress * progress
}

const createDriftEventSource = (token: string) => {
    return createAuthenticatedEventSource(`${BASE_URL}/drift/data/stream?token=${token}`)
}

const startDriftListening = async () => {
    await DriftService.start()
}

const stopDriftListening = async () => {
    await DriftService.stop()
}

export const DriftHud = () => {
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [driftData, setDriftData] = useState<DriftDto | null>(null)

    useEffect(() => {
        const token = localStorage.getItem("accessToken") || ""
        if (!token) {
            console.error("Drift token not found")
            return
        }

        let isUnmounted = false
        let eventSource: EventSource | null = null

        const onInit = (event: Event) => {
            const e = event as MessageEvent
            console.log("DRIFT SSE connected:", e.data)
        }

        const onDriftUpdate = (event: Event) => {
            try {
                const e = event as MessageEvent
                const dto = JSON.parse(e.data) as DriftDto
                setDriftData(dto)
            } catch (error) {
                console.error("Failed to parse DRIFT_UPDATE:", error)
            }
        }

        const connect = async () => {
            try {
                if (isUnmounted) return
                await startDriftListening()
                if (isUnmounted) return

                eventSource = createDriftEventSource(token)
                eventSource.addEventListener("INIT", onInit as EventListener)
                eventSource.addEventListener("DRIFT_UPDATE", onDriftUpdate as EventListener)

                eventSource.onerror = (error) => {
                    console.error("Drift SSE error:", error)
                }
            } catch (error) {
                toast.error(t.error_unknown, {
                    className: "notification-error"
                })
            }
        }

        connect()

        return () => {
            isUnmounted = true

            if (eventSource) {
                eventSource.removeEventListener("INIT", onInit as EventListener)
                eventSource.removeEventListener("DRIFT_UPDATE", onDriftUpdate as EventListener)
                eventSource.close()
            }

            stopDriftListening()
        }
    }, [])

    const {
        isShown, score, scoreToShow, footerAnimation,
        isFinalVisible, finalScore, playMultiplierShine,
        achievementTitle, achievementPoints, totalScore, multiplier,
        isPairActive, pairBonusScore, pairStreakSec,
    } = useDriftHudState({ driftData, unsuccessedText: t.unsucceded })

    const [normalizedAngle, setNormalizedAngle] = useState(0)

    const arrowPosition = useMemo(() => {
        const t = (normalizedAngle + 90) / 180

        const p0 = { x: 40, y: 48 }
        const p1 = { x: 280, y: 184 }
        const p2 = { x: 520, y: 48 }

        const oneMinusT = 1 - t

        const x =
            oneMinusT * oneMinusT * p0.x +
            2 * oneMinusT * t * p1.x +
            t * t * p2.x

        const y =
            oneMinusT * oneMinusT * p0.y +
            2 * oneMinusT * t * p1.y +
            t * t * p2.y

        const dx =
            2 * oneMinusT * (p1.x - p0.x) +
            2 * t * (p2.x - p1.x)

        const dy =
            2 * oneMinusT * (p1.y - p0.y) +
            2 * t * (p2.y - p1.y)

        const tangentAngle = Math.atan2(dy, dx) * 180 / Math.PI

        return {
            x,
            y,
            angle: tangentAngle
        }
    }, [normalizedAngle])

    const pathRef = useRef<SVGPathElement | null>(null)

    const arrowLengthPercent = useMemo(() => {
        return (normalizedAngle + 90) / 180
    }, [normalizedAngle])

    useEffect(() => {
        ClientApi.on(NuiMessages.DRIFT_UPDATE, ({ angle }: { angle: number }) => {
            setNormalizedAngle(parseInt(angle.toFixed(1)))
        })
    }, [])

    useEffect(() => {
        const path = pathRef.current
        if (!path) return

        const totalLength = path.getTotalLength()
        const targetLength = totalLength * arrowLengthPercent

        const point = path.getPointAtLength(targetLength)

        const prevPoint = path.getPointAtLength(Math.max(targetLength - 1, 0))
        const nextPoint = path.getPointAtLength(Math.min(targetLength + 1, totalLength))

        const tangentAngle =
            Math.atan2(nextPoint.y - prevPoint.y, nextPoint.x - prevPoint.x) * 180 / Math.PI

    }, [arrowLengthPercent])

    const titleStyle = {
        ["--shake-animation-delta" as string]: `${getShakeAnimationDelta(totalScore)}px`,
        animation: `shake ${getShakeAnimationDuration(totalScore)}ms infinite`,
        transform: "translateX(0px) translateY(0px)"
    } as CSSProperties

    const textContainerClass = playMultiplierShine
        ? "drift-hud-header-text-container drift-hud-header-text-container-in shine-wider-anim"
        : "drift-hud-header-text-container drift-hud-header-text-container-out"

    if (!isShown) {
        return <div className="drift-hud-wrapper" />
    }

    return (
        <div className="drift-hud-wrapper">
            <div className="drift-hud-header">
                {!isFinalVisible ? (
                    <>
                        <div
                            className="drift-hud-header-title-container"
                            style={titleStyle}
                        >
                            <p className="drift-hud-header-title-points">
                                {totalScore}
                            </p>

                            <div className={playMultiplierShine ? "shine-anim" : ""}>
                                <div className="drift-hud-header-title-inner-container">
                                    <p style={{ fontSize: "1.5rem" }}>x</p>
                                    <p>{multiplier.toFixed(1)}</p>
                                </div>
                            </div>
                        </div>

                        <div className={textContainerClass}>
                            <p>{getAchievementTitle(achievementTitle, t)}</p>
                            <p style={{ color: "#FFFF6F" }}>{achievementPoints}</p>
                        </div>

                        <PairDriftBadge
                            isPairActive={isPairActive}
                            pairBonusScore={pairBonusScore}
                            pairStreakSec={pairStreakSec}
                        />
                    </>
                ) : (
                    <div className="shine-final-anim">
                        <p className="drift-hud-header-final-title-points">
                            {finalScore}
                        </p>
                    </div>
                )}
            </div>

            <div className={`drift-hud-footer ${footerAnimation}`}>
                <div className="drift-angle-meter-wrapper">
                    <div className="drift-angle-meter-panel">
                        <div className="drift-angle-meter">
                            <svg
                                className="drift-angle-meter-svg"
                                viewBox="0 0 560 220"
                                aria-hidden="true"
                            >
                                <path
                                    className="drift-angle-meter-track"
                                    d="M 40 48 Q 280 184 520 48"
                                />

                                <g
                                    transform={`translate(${arrowPosition.x} ${arrowPosition.y}) rotate(${arrowPosition.angle})`}
                                >
                                    <polygon
                                        className="drift-angle-meter-arrow"
                                        points="0,0 -12,22 12,22"
                                    />
                                </g>
                            </svg>

                            <div className="drift-angle-meter-value">
                                {normalizedAngle}°
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}